import { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router";
import { TopNav } from "./TopNav";
import { Footer } from "./Footer";
import { useTranslation } from "@/lib/i18n";

/**
 * Public-shell layout wrapper for every Phase 2 (and later) route.
 *
 * Composition (top → bottom):
 *   1. Skip-link "Skip to content" — first focusable element; lands on <main id="main">.
 *   2. <TopNav /> — wordmark + 4-tab nav (auth-adaptive).
 *   3. <main id="main"> — renders nested <Outlet /> for the matched child route.
 *   4. <Footer /> — 3-column grid on desktop, stacked on mobile.
 *
 * Background uses the `bg-surface-canvas` token introduced in 02-01 so catalog,
 * detail, and placeholder pages all share the soft blue-gray page background.
 */
export function PublicShell() {
  const location = useLocation();
  const { t } = useTranslation();
  const isConsultations = location.pathname === "/consultations";
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport || !isConsultations) return;

    const handleResize = () => {
      if (containerRef.current && window.visualViewport) {
        containerRef.current.style.height = `${window.visualViewport.height}px`;
      }
    };

    window.visualViewport.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, [isConsultations]);

  return (
    <div
      ref={containerRef}
      className={
        isConsultations
          ? "flex overflow-hidden flex-col bg-surface-canvas w-full transition-[height] duration-250 ease-out"
          : "flex min-h-dvh flex-col bg-surface-canvas w-full"
      }
    >
      <a
        href="#main"
        // Quick task 260512-q7q — promote the skip-link to the new shadow
        // scale and give it a soft border so it reads as a deliberate badge
        // when it pops out of sr-only.
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-background focus:px-3 focus:py-2 focus:text-sm focus:text-foreground focus:shadow-elev-2 focus:border focus:border-border/60"
      >
        {t("skip_to_content")}
      </a>
      <TopNav />
      {/* Quick task 260512-q7q — tiny top breath so content doesn't kiss
          the sticky-nav border. */}
      <main
        id="main"
        className={
          isConsultations
            ? "flex-1 overflow-hidden flex flex-col pt-0 sm:pt-2"
            : "flex-1 pt-2"
        }
      >
        <Outlet />
      </main>
      {!isConsultations && <Footer />}
    </div>
  );
}
